import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, GiftIcon, MessageCircleIcon } from 'lucide-react';
import { PurchaseOptions } from '../shared/PurchaseOptions';
import { useFeatures } from '../../hooks/useFeatures';
import { canSubscribe } from '../../core/rules/features';
import { boxRange, chooseCadence, planBoxes } from '../../core/rules/refillPlan';
import { refillItemsFromProducts, refillRules } from '../../utils/refill';
import { today } from '../../core/format/dates';
import { policyCopy } from '../../data/policies';
import { capitalize } from '../../core/format/text';
import { MediaView } from '../shared/MediaView';
import { ProductImage } from '../../ui/ProductImage';
import { useCatalog } from '../../contexts/CatalogContext';
import { useCartActions } from '../../contexts/StoreContext';
import { usePages, useSite } from '../../contexts/SiteContext';
import { unlockedIn } from '../../data/gifts';
import { shopLabels } from '../../data/shopLabels';
import { founder, salon } from '../../data/content';
import { euro } from '../../core/format/currency';
import { countSoldOut, isAllSoldOut, isSoldOut } from '../../core/rules/stock';
import { fill } from '../../utils/copy';
import { sectionHeading } from '../../ui/sections';
import { Section } from '../shared/Section';
import { btnPrice, btnPriceAmount, btnPriceLabel } from '../../ui/styles';
import type { Routine } from '../../core/types/catalog';
const primary = btnPrice;

/**
 * Het anker van de pagina: het complete protocol, op blush. Bewust geen protocolkaart in een
 * kader (dan stond het verhaal er twee keer), maar één uitgeschreven blok: het beeld blijft staan
 * (computer), rechts het verhaal, de producten in volgorde van gebruik, wat erbij zit en de
 * cadeaus die je met dit protocol vrijspeelt. Eén knop, met de prijs erin.
 */
export function RoutineAnchor({
  routine
}: {
  routine: Routine;
}) {
  const {
    routineTotals
  } = useCatalog();
  const {
    addRoutine
  } = useCartActions();
  const {
    items,
    price,
    months
  } = routineTotals(routine);
  const site = useSite();
  const pages = usePages();
  const gifts = unlockedIn(site.giftTiers, site.policy.freeShippingFrom, price);
  const soldOutCount = countSoldOut(items);
  const allSoldOut = isAllSoldOut(items);
  const features = useFeatures();
  const canRefill = items.some(p => canSubscribe(features, p));
  const [subscribe, setSubscribe] = useState(false);
  // Slim bijvullen: het kortste ritme waarbij elke doos het minimum haalt, en wat een doos meestal kost.
  const start = today();
  const refillItems = refillItemsFromProducts(items.filter(p => canSubscribe(features, p) && !isSoldOut(p)).map(product => ({
    product
  })), start);
  const rules = refillRules(site.policy.subscription);
  const rhythm = chooseCadence(refillItems, rules, start);
  const range = rhythm === null ? null : boxRange(planBoxes(refillItems, {
    intervalWeeks: rhythm,
    rules,
    earliest: start,
    count: 6
  }));
  const refillMeta = range ? `Elke ${rhythm} weken een doos met wat op raakt, ${range.min === range.max ? euro(range.min) : `meestal ${euro(range.min)} tot ${euro(range.max)}`}. ${capitalize(policyCopy.subscriptionPerks)}.` : '';
  return <Section tone="blush" labelledBy="anchor-routine" contained={false} data-id="element-8494" data-mp="30opbcjqeqctd">
      <div className="mx-auto grid max-w-content gap-10 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-8495" data-mp="2vnji8ksu4njb">
        <div className="lg:col-span-5" data-id="element-8496" data-mp="2rnhdu3vptg0c">
          <div className="lg:sticky lg:top-28" data-id="element-8497" data-mp="3obnqne2sjepp">
            <MediaView media={{
            image: routine.image || founder.withProducts,
            alt: ''
          }} sizes="(min-width: 1024px) 38vw, 100vw" className="aspect-[4/3] w-full sm:aspect-[16/10] lg:aspect-[4/5]" data-id="element-8498" data-mp="3obfc7hr0s1ce" />
          </div>
        </div>

        <div className="lg:col-span-7" data-id="element-8499" data-mp="3ejre4xzyjm1y">
          <p className="text-[14px] text-ink" data-id="element-8500" data-mp="3albwl9mnb5vx">{fill(pages.routines.anchorEyebrow)}</p>
          <h2 id="anchor-routine" className={`mt-4 ${sectionHeading} text-ink`} data-id="element-8501" data-mp="awsjddt514wr">
            {routine.name}
          </h2>
          <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-ink" data-id="element-8502" data-mp="659cpf4iv4rb">{routine.story}</p>
          {routine.forWho && <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-muted" data-id="element-8503" data-mp="3msx4a0ywsolf">Voor wie: {routine.forWho.toLowerCase()}.</p>}

          {/* De volgorde is de boodschap: zo gebruik je het, van reinigen tot bescherming. */}
          <h3 className="mt-10 text-[14px] text-ink" data-id="element-8504" data-mp="3dv3d14g04l54">In volgorde van gebruik</h3>
          <ol className="mt-3 divide-y divide-ink/15 border-y border-ink/15" data-id="element-8505" data-mp="3hv41zj0q8eqf">
            {items.map((p, i) => <li key={p.id} className="flex items-center gap-4 py-3" data-id="element-8506" data-mp="9fk9wnbsl2cy" data-mpi={i}>
                <span aria-hidden="true" className="w-5 shrink-0 type-name text-muted tabular-nums" data-id="element-8507" data-mp="eck86c2eh0ti">
                  {i + 1}
                </span>
                <ProductImage src={p.image} alt="" width={48} className="h-14 w-12 shrink-0 bg-shell p-1" data-id="element-8508" data-mp="4o2cg98z9jwq" />
                <div className="min-w-0 flex-1" data-id="element-8509" data-mp="sdwoiolh34fx">
                  <p className="text-[12px] text-ink" data-id="element-8510" data-mp="sc3gb4v7oc5t">{isSoldOut(p) ? shopLabels.soldOutShort : p.step}</p>
                  <Link to={`/webshop/${p.id}`} className="text-[15px] leading-snug text-ink underline-offset-4 hover:underline" data-id="element-8511" data-mp="x5o4k5705rrt">
                    {p.name}
                  </Link>
                </div>
                <span className={`text-[14px] tabular-nums text-muted ${isSoldOut(p) ? 'line-through' : ''}`} data-id="element-8512" data-mp="2cvfbb45jqsqa">{euro(p.price)}</span>
              </li>)}
          </ol>

          <div className="mt-8 grid gap-8 sm:grid-cols-2" data-id="element-8513" data-mp="2mmteblwpiao6">
            {routine.included.length > 0 && <div data-id="element-8514" data-mp="1ywfuujs6tk57">
                <h3 className="text-[14px] text-ink" data-id="element-8515" data-mp="1yy0irqjzy4u3">Kosteloos inbegrepen</h3>
                <ul className="mt-3 space-y-2" data-id="element-8516" data-mp="1tye3lbklyein">
                  {routine.included.map(inc => <li key={inc} className="flex items-start gap-2.5 text-[15px] leading-snug text-ink" data-id="element-8517" data-mp="28vew3hamidi6">
                      <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-sage" strokeWidth={2} aria-hidden="true" data-id="element-8518" data-mp="23tplu3v863v5" />
                      {inc}
                    </li>)}
                </ul>
              </div>}
            {gifts.length > 0 && <div data-id="element-8519" data-mp="3mckpnfjzpb27">
                <h3 className="text-[14px] text-ink" data-id="element-8520" data-mp="3md44uoi99lcj">{fill(pages.routines.gifts.heading)}</h3>
                <ul className="mt-3 space-y-2" data-id="element-8521" data-mp="3ram8bzdi78d5">
                  {gifts.map(g => <li key={g.ruleId} className="flex items-start gap-2.5 text-[15px] leading-snug text-ink" data-id="element-8522" data-mp="6p70pe4c7zp">
                      <GiftIcon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-8523" data-mp="54h6q5l08yeg" />
                      {g.title}
                    </li>)}
                </ul>
              </div>}
          </div>

          <div className="mt-10 max-w-md" data-id="element-8524" data-mp="3dkiu9vos2kys">
            <p className="mb-3 text-[14px] text-muted" data-id="element-8525" data-mp="3djr5p4tllni0">
              Gaat {months} mee · dezelfde prijs als los, zonder giswerk
            </p>
            {/* Eenmalig of bijvullen: standaard eenmalig, nooit stiekem. Het ritme volgt hoe lang het protocol meegaat. */}
            {canRefill && !allSoldOut && rhythm !== null && <div className="mb-4" data-id="element-8526" data-mp="170daqxfowaal">
                <PurchaseOptions name={`routine-${routine.id}`} legend={`Eenmalig of bijvullen: ${routine.name}`} price={price} value={subscribe} onChange={setSubscribe} options={[{
              value: false,
              title: 'Eenmalig',
              meta: `Eén keer het complete protocol. ${months} vooruit.`
            }, {
              value: true,
              title: 'Bijvullen wanneer het op is',
              meta: `${refillMeta} Stoppen wanneer je wilt.`
            }]} data-id="element-8527" data-mp="16zbw6izgt5o4" />
              </div>}
            {allSoldOut ? <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={`${primary} justify-center`} data-id="element-8528" data-mp="f9fjlxsz0xr8">
                {shopLabels.soldOutShort} · vraag {founder.firstName} om een alternatief
                <span className="sr-only" data-id="element-8529" data-mp="5d41gr6p9dov">{shopLabels.viaWhatsApp}</span>
              </a> : <button type="button" onClick={() => addRoutine(routine.id, subscribe && rhythm !== null ? {
            subscribe: true,
            intervalWeeks: rhythm
          } : undefined)} className={primary} data-id="element-8530" data-mp="e98wxixkark">
                <span className={btnPriceLabel} data-id="element-8531" data-mp="1f8psyw3y4hac">
                  Protocol in de mand<span className="sr-only" data-id="element-8532" data-mp="1k712t6u8qrsk">: {routine.name}</span>
                </span>
                <span className={btnPriceAmount} data-id="element-8533" data-mp="1k8u5yc5natvt">{euro(price)}</span>
              </button>}
            {soldOutCount > 0 && !allSoldOut && <p className="mt-3 text-[13px] leading-relaxed text-muted" data-id="element-8534" data-mp="1a9v9rt22gqpp">{shopLabels.partlySoldOut(soldOutCount)}</p>}
            {/* Twijfel? Eén rustige regel onder de knop, in plaats van een eigen blok "zo werkt het". */}
            {pages.routines.gifts.note && <a href={salon.whatsapp} target="_blank" rel="noreferrer" className="mt-4 flex items-start gap-2 text-[14px] leading-relaxed text-muted underline-offset-4 transition-colors duration-150 ease-soft hover:text-ink hover:underline" data-id="element-8535" data-mp="mjisvsoufjmz">
                <MessageCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-ink" strokeWidth={1.5} aria-hidden="true" data-id="element-8536" data-mp="3vqhfffnzukn" />
                {fill(pages.routines.gifts.note)}
                <span className="sr-only" data-id="element-8537" data-mp="cvmzgy7jtv6q">{shopLabels.viaWhatsApp}</span>
              </a>}
          </div>
        </div>
      </div>
    </Section>;
}